import { useState, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";
import { ToggleBox, Tooltip } from "@unified-checkout/design-system";
import {
  SHARED_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/shared";
import {
  TEST_LIVE_MODE_EVENTS
} from "@app/dashboard/constants/tracking_events/test-live-modes";
import { MODES } from "@app/dashboard/constants/modes";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useIntegrationsStore } from "@app/dashboard/store/integrations";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { isTestLiveModeDisabled } from "@app/dashboard/utils/getLiveTestSupportedRoutes";


const ModeTogglerComponent = ({ onTestToggleChange }: { onTestToggleChange: (checked: boolean) => void }) => {
  const [isTouched, setIsTouched] = useState(false);
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const { hasLiveIntegration } = useIntegrationsStore();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const path = window.location.pathname;

  const isDisabled = isTestLiveModeDisabled(path);
  const text = isDisabled ? t('innerLayout.status.testLiveModeNotSupported') : '';

  const handleToggleChange = useCallback((checked: boolean) => {
    onTestToggleChange(checked);

    if (!isTouched) return;

    const mode = checked ? MODES.TEST : MODES.LIVE;

    amplitude.track(TEST_LIVE_MODE_EVENTS.TOGGLED, {
      [SHARED_EVENTS_PARAMS.MODE]: mode,
      [SHARED_EVENTS_PARAMS.FROM_PAGE]: path
    });

  }, [isTouched, onTestToggleChange, path]);

  const handleBeforeToggleConfirmation = useCallback(() => {
    setIsTouched(true);
    return Promise.resolve(true);
  }, [setIsTouched]);  

  return (
    <Tooltip
      placement={isMobile ? "top start" : "bottom center"}
      text={text}
    >
      <ToggleBox
        color="warning"
        size="md"
        text={t('innerLayout.navbar.testMode')}
        type="transparent box"
        checked={!isLive}
        onToggleChangeHandler={handleToggleChange}
        disabled={!hasLiveIntegration || isDisabled}
        onBeforeToggleConfirmation={handleBeforeToggleConfirmation}
      />
    </Tooltip>
  )
};

export const ModeToggler = memo(ModeTogglerComponent);
